import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Navbar from "../../components/ui/Navbar";
import Footer from "../../components/ui/Footer";
import { authApi } from "../../services/api";
import { useAuth } from "../../hooks/useAuth";

const CheckIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const HIGHLIGHTS = [
  "Pick up your drafts and chapters right where you left off.",
  "Generate new sections, edit content, and track your progress.",
  "Access topic ideas, diagrams, and full documentation in one workspace.",
  "Secure account — your project work stays private to you.",
];

const inputClass = (hasError) =>
  `w-full rounded-xl border bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:outline-none focus:ring-2 ${
    hasError
      ? "border-red-300 focus:border-red-400 focus:ring-red-100"
      : "border-slate-200 focus:border-[#0BBF64] focus:ring-[#0BBF64]/20"
  }`;

const FormField = ({ label, name, type = "text", value, onChange, error, placeholder }) => (
  <div>
    <label htmlFor={name} className="mb-1.5 block text-sm font-medium text-slate-700">
      {label}
      <span className="text-[#0BBF64]"> *</span>
    </label>
    <input
      id={name}
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      className={inputClass(!!error)}
      placeholder={placeholder}
    />
    {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
  </div>
);

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
    }
  }, [location]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (apiError) setApiError("");
    if (successMessage) setSuccessMessage("");
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    setApiError("");

    try {
      const response = await authApi.login({
        email: formData.email,
        password: formData.password,
      });

      if (response.data && response.data.message === "successful") {
        const userData = response.data.data;
        localStorage.setItem("user", JSON.stringify(userData));
        localStorage.setItem("isAuthenticated", "true");
        login(userData);
        navigate("/dashboard");
      } else {
        setApiError(
          response.data?.message || "Login failed. Please try again."
        );
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Login failed. Please check your credentials and try again.";
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900">
      <Navbar />

      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10 lg:py-16">
          <div className="grid gap-8 lg:grid-cols-2 lg:gap-12 lg:items-stretch">
            {/* Marketing panel */}
            <section className="relative order-2 overflow-hidden rounded-2xl lg:order-1">
              <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900" />
              <div
                aria-hidden
                className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:2.5rem_2.5rem]"
              />
              <div
                aria-hidden
                className="absolute -bottom-16 -left-16 h-56 w-56 rounded-full bg-[#0BBF64]/20 blur-3xl"
              />

              <div className="relative flex h-full flex-col justify-center p-8 sm:p-10 lg:p-12">
                <p className="text-xs font-semibold uppercase tracking-wider text-emerald-300">
                  Welcome back
                </p>
                <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
                  Continue your project documentation
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-slate-300">
                  Log in to access your workspace, drafts, and AI-generated
                  chapters — all in one place.
                </p>

                <ul className="mt-8 space-y-4">
                  {HIGHLIGHTS.map((text) => (
                    <li key={text} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-[#0BBF64]">
                        <CheckIcon />
                      </span>
                      <span className="text-sm leading-relaxed text-slate-200">
                        {text}
                      </span>
                    </li>
                  ))}
                </ul>

                <p className="mt-8 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-xs leading-relaxed text-slate-400 backdrop-blur-sm">
                  New to Astudent?{" "}
                  <Link
                    to="/signup"
                    className="font-semibold text-emerald-300 hover:text-emerald-200"
                  >
                    Create a free account
                  </Link>{" "}
                  — no credit card required.
                </p>
              </div>
            </section>

            {/* Form panel */}
            <section className="order-1 flex flex-col justify-center lg:order-2">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#0BBF64]">
                    Log in
                  </p>
                  <h1 className="mt-1 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                    Welcome back
                  </h1>
                </div>
                <Link
                  to="/signup"
                  className="hidden text-sm text-slate-600 transition hover:text-slate-900 sm:block"
                >
                  Sign up
                </Link>
              </div>

              <p className="mb-6 text-sm text-slate-600">
                Enter your credentials to access your dashboard and projects.
              </p>

              {successMessage && (
                <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                  {successMessage}
                </div>
              )}

              {apiError && (
                <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {apiError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <FormField
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={errors.email}
                  placeholder="you@example.com"
                />

                <div>
                  <FormField
                    label="Password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    error={errors.password}
                    placeholder="Enter your password"
                  />
                  <div className="mt-2 text-right">
                    <Link
                      to="/forgot-password"
                      className="text-sm font-medium text-[#0BBF64] transition hover:text-[#0aa459]"
                    >
                      Forgot password?
                    </Link>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex w-full items-center justify-center rounded-xl bg-[#0BBF64] px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/20 transition hover:bg-[#0aa459] hover:shadow-emerald-500/30 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? "Signing in…" : "Log in"}
                </button>

                <p className="text-center text-sm text-slate-600 sm:hidden">
                  Don&apos;t have an account?{" "}
                  <Link
                    to="/signup"
                    className="font-semibold text-[#0BBF64] hover:text-[#0aa459]"
                  >
                    Sign up free
                  </Link>
                </p>
              </form>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Login;
